<template>
  <b-modal
    centered
    v-if="isOpen"
    v-model="isOpen"
    cancel-disabled
    hide-footer
    :title="`Logins realizados para o usuário - ${user.email}`"
    size="xl"
    @close="close"
  >
    <b-row>
      <b-col
        cols="12"
        class="d-flex justify-content-end mb-2"
        style="gap: 0.5rem"
      >
        <b-button
          variant="outline-danger"
          size="sm"
          @click="$refs.expirePopover.open()"
          :disabled="loading"
          v-b-tooltip.hover
          title="Expirar acesso do usuário"
          id="expireButton"
        >
          <i class="fas fa-user-lock mx-1"></i>
        </b-button>
        <ConfirmationExpire
          ref="expirePopover"
          targetId="expireButton"
          :targetEmail="user.email"
          @expire="expire"
        />
        <b-button
          v-b-tooltip.hover
          title="Atualizar lista de logins"
          variant="primary"
          :disabled="loading"
          @click="getLogins"
        >
          <i class="fas fa-sync-alt"></i>
        </b-button>
      </b-col>
      <b-col cols="12">
        <b-table
          striped
          responsive
          hover
          show-empty
          bordered
          :fields="table.headers"
          :items="table.records"
          :busy="loading"
        >
          <template #empty>
            <div class="text-center">Nenhum login encontrado para o usuário</div>
          </template>
          <template #cell(isExpired)="{ item }">
            <span>
              <b-badge
                variant="primary"
                v-if="!item.solicitation.isExpired"
                v-b-tooltip
                :title="`Expira em: ${new Date(
                  item.solicitation.expiresAt
                ).toLocaleString('pt-BR')}`"
              >
                Não
              </b-badge>
              <b-badge
                variant="danger"
                v-else
                :title="`Expirado em: ${new Date(
                  item.solicitation.expiresAt
                ).toLocaleString('pt-BR')}`"
                v-b-tooltip
              >
                Sim
              </b-badge>
            </span>
          </template>
          <template #cell(userAgent)="{ item }">
            <span v-b-tooltip :title="item.userAgent">
              {{ textTruncate(item.userAgent, 25) }}
            </span>
          </template>
        </b-table>
        <b-pagination
          v-model="pagination.currentPage"
          :total-rows="pagination.total"
          :per-page="pagination.itemsPerPage"
          :disabled="loading"
          align="center"
          pills
        ></b-pagination>
      </b-col>
    </b-row>
  </b-modal>
</template>

<script>
import api from '@/api';
import makeToast from 'Mixins/makeToast';
import { mapGetters } from 'vuex';
import { textTruncate } from '@/utils/string';
import ConfirmationExpire from './ConfirmationExpire.vue';

export default {
  name: 'AccessUserModal',
  mixins: [makeToast],
  data() {
    return {
      loading: false,
      isOpen: false,
      user: {},
      table: {
        records: [],
        headers: [
          {
            key: 'ipAddress',
            label: 'IP',
          },
          {
            key: 'solicitation.requesterUser.email',
            label: 'Criada por',
          },
          {
            key: 'isExpired',
            label: 'Expirado',
          },
          {
            key: 'solicitation.id',
            label: 'Solicitação ID',
          },
          {
            key: 'loggedAt',
            label: 'Login em',
            formatter(value) {
              return value ? new Date(value).toLocaleString('pt-BR') : '';
            },
          },
          {
            key: 'solicitation.createdAt',
            label: 'Solicitação em',
            formatter(value) {
              return value ? new Date(value).toLocaleString('pt-BR') : '';
            },
          },
          {
            key: 'solicitation.expiresAt',
            label: 'Expira em',
            formatter(value) {
              return value ? new Date(value).toLocaleString('pt-BR') : '';
            },
          },
          {
            key: 'userAgent',
            label: 'Navegador utilizado',
          },
        ],
      },
      pagination: {
        currentPage: 1,
        itemsPerPage: 10,
        total: 0,
        lastPage: 0,
      },
    };
  },
  computed: {
    ...mapGetters(['getToken']),
  },
  watch: {
    'pagination.currentPage': function () {
      this.getLogins();
    },
  },
  methods: {
    textTruncate,
    open(user) {
      this.isOpen = true;
      this.user = user;
      this.getLogins();
    },
    close() {
      this.isOpen = false;
      this.user = {};
      this.table.records = [];
      this.pagination.currentPage = 1;
      this.pagination.total = 0;
    },
    async getLogins() {
      this.loading = true;

      try {
        const queryParams = new URLSearchParams({
          targetUserId: this.user.id,
          page: this.pagination.currentPage,
          perPage: this.pagination.itemsPerPage,
        });

        const response = await api.get(
          `/v1/admin/impersonate/logins?${queryParams.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${this.getToken}`,
            },
          }
        );
        const { total, data } = response.data;
        this.table.records = data || [];
        this.pagination.total = total;
      } catch (error) {
        this.makeToast(
          'Erro',
          `Houve um erro ao buscar os logins do usuário "${this.user.email}": "${error.message}"`,
          'danger'
        );
      } finally {
        this.loading = false;
      }
    },
    async expire() {
      this.loading = true;

      try {
        await api.put(
          '/v1/admin/impersonate/expire',
          {
            targetUserId: this.user.id,
          },
          {
            headers: {
              Authorization: `Bearer ${this.getToken}`,
            },
          }
        );

        this.getLogins();

        this.makeToast(
          'Sucesso',
          `Acesso do usuário "${this.user.email}" bloqueado com sucesso`,
          'success'
        );
      } catch (error) {
        this.makeToast(
          'Erro',
          `Houve um erro ao expirar os acessos ao usuário "${this.user.email}": "${error.message}"`,
          'danger'
        );
      } finally {
        this.loading = false;
      }
    },
  },
  components: {
    ConfirmationExpire,
  },
};
</script>
